import {
  IProvidedTranslationProps,
  withTranslations,
} from '@wix/wixstores-client-common-components/dist/es/src/outOfIframes/translations';
import {VIEW_MORE_OPTIONS_URL_FRAGMENT} from '@wix/wixstores-client-storefront-sdk/dist/src/constants';
import {ProductOptionType} from '@wix/wixstores-graphql-schema/dist/es/src/graphql-schema';
import classNames from 'classnames';
import React from 'react';
import type {BoxSelectionProps, DropdownOptionProps, DropdownProps} from 'wix-ui-tpa/cssVars';
import {BoxSelection, BoxSelectionBoxSize, Spinner, SpinnerTypes} from 'wix-ui-tpa/cssVars';
import {Experiments, TextualOptionType} from '../../../../../constants';
import {useGlobals, withGlobals} from '../../../../../globalPropsContext';
import {IProduct} from '../../../../../types/galleryTypes';
import {LazyDropDown} from '../../../../lazyComponents/lazyWUTComponents';
import {classes as dropdownOptionStyles, style as styleDropdown} from '../DropdownOption.st.css';
import {DataHook, ProductOptionsChangeData} from '../ProductOptions';
import {classes as boxSelectionStyles} from './ProductTextualOption.st.css';
import s from './ProductTextualOptions.scss';
import {useOptionSelectionLogic} from '../../hooks/options/useOptionSelectionLogic';

type IProductOption = IProduct['options'][0];

const renderInnerSelection = (
  selection: DropdownOptionProps,
  index: number,
  styles: Record<string, string>,
  isDisabled: boolean,
  isSelected: boolean
) => {
  return (
    <div
      data-hook={`box-selection-text-option-${index}`}
      className={classNames(styles.optionItemText, {
        [styles.disabled]: isDisabled,
        [styles.selected]: isSelected,
      })}>
      {selection.value}
    </div>
  );
};

const getShownSelectionsAndSuffix = ({
  visibleSelections,
  showButtons,
  productOptionButtonLimit,
  productUrl,
  isLiveSiteMode,
  isPreviewMode,
  styles,
  t,
}: {
  visibleSelections: IProductOption['selections'];
  showButtons: boolean;
  productOptionButtonLimit: number;
  productUrl: string;
  isLiveSiteMode: boolean;
  isPreviewMode: boolean;
  styles: Record<string, string>;
  t: IProvidedTranslationProps['t'];
}) => {
  const shownSelections = showButtons ? visibleSelections.slice(0, productOptionButtonLimit) : visibleSelections;
  const remainingOptionsCounter = visibleSelections.length - productOptionButtonLimit;
  const productUrlObj = new URL(`${productUrl}#${VIEW_MORE_OPTIONS_URL_FRAGMENT}-1`);
  const suffix =
    remainingOptionsCounter <= 0 ? null : (
      <a
        dir="ltr"
        href={isLiveSiteMode || isPreviewMode ? productUrlObj.href : null}
        data-hook={DataHook.ButtonOptionMore}
        className={styles.remainingOptionsCounter}
        aria-label={t('buttonOptionNumMoreOptionsAriaLabel', {numRemainingChoices: remainingOptionsCounter})}>
        {t('buttonOptionNumChoicesLeftLabel', {numChoiceLeft: remainingOptionsCounter})}
      </a>
    );
  return {
    suffix,
    shownSelections,
  };
};

export interface TextualOptionProps {
  option: IProductOption;
  availability: Record<string, {isVisible: boolean; isSelectable: boolean}>;
  selectionIds: number[];
  readOnly?: boolean;
  isLoading: boolean;
  productUrl: string;
  onSelectionIdsChange: (optionSelections: number[], changeData: ProductOptionsChangeData) => Promise<void>;
}

const TextualOption: React.FC<TextualOptionProps & IProvidedTranslationProps> = ({
  option,
  availability,
  selectionIds,
  readOnly,
  isLoading,
  productUrl,
  onSelectionIdsChange,
  t,
}) => {
  const {useExperiments, shouldShowMobile, styles, stylesParams, isLiveSiteMode, isPreviewMode} = useGlobals();
  const shouldUseNativeDropdownOnMobile = useExperiments.enabled(Experiments.GalleryProductOptionMobileNativeDropdown);
  const isButtonOptionsEnabled = useExperiments.enabled(Experiments.GalleryProductOptionsTextualOptionType);
  const textualOptionType = styles.get(stylesParams.gallery_textualOptionType);
  const showButtons = isButtonOptionsEnabled && textualOptionType === TextualOptionType.BUTTONS;

  const {visibleSelections, selectedSelectionId, calculateNextSelectionIds, isSelectionDisabled, isSelectionChecked} =
    useOptionSelectionLogic({
      option,
      selectionIds,
      selectionsAvailability: availability,
    });

  const handleOptionChange = (id: string) => {
    const pickedSelectionId = parseInt(id, 10);
    const nextSelectionIds = calculateNextSelectionIds(pickedSelectionId);
    void onSelectionIdsChange(nextSelectionIds, {
      optionType: ProductOptionType.DROP_DOWN,
    });
  };

  const sealButtonChangeHandler: BoxSelectionProps['onChange'] = ({id}) => {
    const parts = id.split('-');
    const selectionId = parts[parts.length - 1];
    handleOptionChange(selectionId);
  };

  const sealDropdownChangeHandler: DropdownProps['onChange'] = ({id}) => {
    handleOptionChange(id);
  };

  const shouldAllowCustomProductOptionsAndQuantityWidth = useExperiments.enabled(
    Experiments.GalleryProductOptionsAndQuantityWidth
  );
  const shouldSeparateQuantityAndOptionsStyleParams = useExperiments.enabled(
    Experiments.ShouldSeparateQuantityAndOptionsStyleParams
  );

  const renderLoader = () => {
    return (
      <div data-hook={DataHook.ProductOptionLoader}>
        <Spinner type={SpinnerTypes.slim} className={s.loader} />
      </div>
    );
  };

  const {title, id} = option;
  const productOptionButtonLimit = styles.get(stylesParams.gallery_productOptionButtonChoicesLimit);
  const {suffix, shownSelections} = getShownSelectionsAndSuffix({
    visibleSelections,
    showButtons,
    productOptionButtonLimit,
    productUrl,
    isLiveSiteMode,
    isPreviewMode,
    styles: s,
    t,
  });

  const options: DropdownOptionProps[] = shownSelections.map((selection) => ({
    id: selection.id.toString(),
    isSelectable: !isSelectionDisabled(selection.id),
    value: selection.description,
  }));

  const renderButtonOptions = () =>
    options.map((selection, index) => {
      const selectionId = parseInt(selection.id, 10);
      const isDisabled = isSelectionDisabled(selectionId);
      const isSelected = !isDisabled && isSelectionChecked(selectionId);

      return (
        <BoxSelection.Option
          key={selection.id}
          id={`${option.id}-${selection.id}`}
          unstableHasInnerWrapper={false}
          checked={isSelected}
          aria-label={selection.value}
          disabled={isDisabled}
          data-hook={`box-selection-option-${index}`}
          className={classNames(boxSelectionStyles.buttonOptionItem, s.buttonOptionItemContainer)}>
          {renderInnerSelection(selection, index, s, isDisabled, isSelected)}
        </BoxSelection.Option>
      );
    });

  const renderBoxSelectionOptions = () => (
    <BoxSelection
      name={`button-option-${id}`}
      aria-label={title}
      size={BoxSelectionBoxSize.xSmall}
      onChange={sealButtonChangeHandler}
      data-hook={DataHook.ButtonOption}
      className={classNames(s.buttonOption, boxSelectionStyles.buttonOptionItem)}
      suffix={suffix}>
      {isLoading ? renderLoader() : renderButtonOptions()}
    </BoxSelection>
  );

  const renderDropdownOptions = () => (
    <LazyDropDown
      className={
        /* istanbul ignore next */
        classNames(
          styleDropdown(dropdownOptionStyles.dropdownOption, {
            separateStyles: shouldSeparateQuantityAndOptionsStyleParams,
          }),
          {
            [s.dropdownWidth]: shouldAllowCustomProductOptionsAndQuantityWidth,
            [s.isMobile]: shouldAllowCustomProductOptionsAndQuantityWidth && shouldShowMobile,
          }
        )
      }
      data-hook={DataHook.DropdownOption}
      aria-label={title}
      placeholder={title}
      options={options}
      selectedId={selectedSelectionId || 'no selection'}
      onChange={sealDropdownChangeHandler}
      isLoading={isLoading}
      readOnly={readOnly}
      mobileNativeSelect={shouldUseNativeDropdownOnMobile && shouldShowMobile}
    />
  );

  return (
    <div
      data-hook={DataHook.ProductOption}
      className={classNames({[s.dropdownAlignment]: shouldAllowCustomProductOptionsAndQuantityWidth})}>
      {showButtons ? renderBoxSelectionOptions() : renderDropdownOptions()}
    </div>
  );
};

export const ProductTextualOption = withGlobals(withTranslations()(TextualOption));
